import apiSlice from 'app/apiSlice';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { sortToString } from 'helpers/paramHelpers';

// Create an entity adapter for users
const campaignsAdapter = createEntityAdapter();

const campaignsApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCampaigns: builder.query({
      query: params => ({
        url: `campaigns`,
        params: {
          ...params,
          sort: sortToString(params.sort || []),
        },
      }),
      transformResponse: response => {
        const normalized = campaignsAdapter.setAll(
          campaignsAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
      providesTags: ['Campaigns'], //tag for refetch when invalidateTags api trggered
    }),

    getCampaignById: builder.query({
      query: id => ({
        url: `campaigns/${id}`,
      }),
      transformResponse: response => {
        return response.data;
      },
      providesTags: ['Campaign'], //tag for refetch when invalidateTags api trggered
      invalidatesTags: ['Campaigns'], //trigger refetch for the providesTags api
    }),

    createCampaign: builder.mutation({
      query: data => ({
        url: `campaigns`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Campaigns', 'Campaign'], //trigger refetch for the providesTags api
    }),

    updateCampaignById: builder.mutation({
      query: ({ id, data }) => ({
        url: `campaigns/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Campaigns', 'Campaign'], //trigger refetch for the providesTags api
    }),

    deleteCampaign: builder.mutation({
      query: id => ({
        url: `campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns', 'Campaign'], //trigger refetch for the providesTags api
    }),

    getRetailers: builder.query({
      query: params => ({
        url: `retailers`,
        params: {
          ...params,
          sort: sortToString(params.sort || []),
        },
      }),
      transformResponse: response => {
        const normalized = campaignsAdapter.setAll(
          campaignsAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
    }),

    submitToRetailers: builder.mutation({
      query: ({ id, data }) => ({
        url: `campaigns/${id}/submit`,
        method: 'POST',
        body: data,
      }),
    }),

    uploadImage: builder.mutation({
      query: data => ({
        url: `creatives`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
  overrideExisting: false, // Avoid overwriting existing endpoints
});

export const {
  useGetCampaignsQuery,
  useGetCampaignByIdQuery,
  useCreateCampaignMutation,
  useUpdateCampaignByIdMutation,
  useDeleteCampaignMutation,
  useGetRetailersQuery,
  useSubmitToRetailersMutation,
  useUploadImageMutation,
} = campaignsApi;

export default campaignsApi;
